import { useEffect, useRef, useState } from 'react'
import USAMap from 'react-usa-map'

interface MapProps {
  data: { [key: string]: number } | undefined
}

const colors = ['#284F43', '#3A6B5D', '#5A8A7C', '#7EB5A8', '#A8D5C9']

const Map = ({ data }: MapProps) => {
  const [stateData, setStateData] = useState<
    { [key: string]: { fill: string; group: number; count: number; title?: string } } | undefined
  >()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [tooltip, setTooltip] = useState<{ x: number; y: number; label: string } | null>(null)

  useEffect(() => {
    if (!data) return

    const maxCount = Math.max(...Object.values(data))
    if (maxCount === 0) return

    const groupSize = maxCount / colors.length

    const mappedData = Object.entries(data).reduce(
      (acc, [state, count]) => {
        const group = Math.max(0, colors.length - Math.ceil(count / groupSize))
        acc[state] = {
          fill: colors[group],
          group,
          count,
          title: `${state}: ${count} ${count === 1 ? 'visit' : 'visits'}`,
        }
        return acc
      },
      {} as { [key: string]: { fill: string; group: number; count: number; title?: string } }
    )

    setStateData(mappedData)
  }, [data])

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current
    if (!container) return

    const rect = container.getBoundingClientRect()
    // Use elementFromPoint for reliable hit-testing over SVG
    const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null
    const withData = el?.closest?.('[data-name], [name], [id]') as HTMLElement | null
    const rawIdentifier =
      withData?.getAttribute('id') ||
      withData?.getAttribute('data-name') ||
      withData?.getAttribute('name') ||
      ''
    const identifier = rawIdentifier.trim()
    const upper = identifier.toUpperCase()

    if (!upper || upper === 'SVG' || upper === 'PATH') {
      setTooltip(null)
      return
    }

    const count = Number((data as any)?.[upper] ?? 0)

    if (count === 0) {
      setTooltip(null)
      return
    }

    const x = e.clientX - rect.left + 10
    const y = e.clientY - rect.top - 20
    setTooltip({ x, y, label: `${upper}: ${count} ${count === 1 ? 'visit' : 'visits'}` })
  }

  const handleMouseLeave = () => setTooltip(null)

  return (
    <div
      className="map"
      ref={containerRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ position: 'relative' }}
    >
      <USAMap title="" defaultFill="#f0f0f0" customize={stateData} />
      {tooltip && (
        <div
          style={{
            position: 'absolute',
            left: tooltip.x,
            top: tooltip.y,
            pointerEvents: 'none',
            background: 'rgba(0,0,0,0.75)',
            color: '#fff',
            padding: '6px 8px',
            borderRadius: 4,
            fontSize: 12,
            whiteSpace: 'nowrap',
            zIndex: 2,
          }}
        >
          {tooltip.label}
        </div>
      )}
    </div>
  )
}

export default Map
